/**
 * Known browser / email-scanner / extension noise. These aren't bugs in
 * our code — they're events other software throws into the page after we
 * load, then bubbles up as window.onerror or unhandledrejection.
 *
 * Filtering here (before insert) keeps the error viewer signal:noise
 * ratio usable. Only applied to window-level unhandled paths, never to
 * explicit clientLogger.error(...) calls — an app-code catch that
 * genuinely sees one of these strings should still surface.
 */

const NOISE_PATTERNS: RegExp[] = [
  // Microsoft Outlook Safe Links / link preview scanner. Outlook opens
  // the URL in a hidden browser to check for phishing; its injected
  // script posts a message to a parent that doesn't exist, throwing
  // this exact rejection into whatever page it landed on. 3.3k hits
  // from 6 sessions in the first 15h post-deploy.
  /Object Not Found Matching Id:\d+, MethodName:(update|apply), ParamCount:\d+/,

  // ResizeObserver benign warnings — Chrome fires these when a layout
  // pass hasn't finished; nothing to fix.
  /ResizeObserver loop (limit exceeded|completed with undelivered notifications)/,

  // Google Translate / other content scripts touching detached DOM.
  /Cannot read properties of null \(reading 'removeChild'\)/,

  // Cross-origin script error. Browsers strip the details for security,
  // leaving only this string — no stack, no line. Nothing we can act on.
  /^Script error\.?$/,

  // Clipboard permission errors — the browser said "no" to
  // navigator.clipboard.readText(). User denied the prompt or the tab
  // isn't focused; not an app bug.
  /Clipboard (read|write) operation is not allowed/,
  /Failed to execute '(readText|writeText|read|write)' on 'Clipboard'/,

  // Android WebView bridge — postMessage into a Java host that already
  // GC'd its handler. Fires when a student navigates away mid-request.
  /Error invoking postMessage: Java (exception was raised|object is gone)/,

  // Safari-specific generic network failure (aborted request / offline
  // / navigated away mid-fetch). Same class as Chrome's "Failed to
  // fetch" — not our bug.
  /^Load failed$/,

  // Legacy PerformanceObserver longtask logger (removed in commit
  // 9677004f). Defensive filter so a stray re-add doesn't flood the
  // table again — long tasks are perf data, not errors.
  /^Long task detected \(\d+ms\)$/,
]

export function isKnownBrowserNoise(message: string): boolean {
  if (!message) return false
  return NOISE_PATTERNS.some((p) => p.test(message))
}
